import { queryOptions, QueryClient } from '@tanstack/react-query';
import type { PermissionPreviewInfo } from '@repo/utils';
import { API, AUTH, BASE, ME, V1 } from '@/lib/constants/urls';

const AUTH_ME_URL = `${BASE}${API}${V1}${AUTH}${ME}`;

interface ImpersonationInfo {
  adminId: string;
  adminUsername: string;
  adminRole: string;
  tenantId: string;
  impersonatedAt: number;
}

export interface AuthMeResponse {
  authenticated: boolean;
  user?: Record<string, unknown> & {
    /**
     * Il login web si può confermare dal telefono collegato, in alternativa
     * alla password (che resta sempre disponibile).
     */
    webLoginApprovalEnabled?: boolean;
  };
  tenants?: Record<string, unknown>[];
  impersonation?: ImpersonationInfo | null;
  /** Anteprima permessi attiva sulla sessione (date ISO: `/auth/me` non passa dal reviver). */
  permissionPreview?: PermissionPreviewInfo | null;
  /**
   * Valorizzato solo nella risposta che ha chiuso l'anteprima (scaduta, target
   * non più valido, sessione revocata…): il client esce e ne mostra il motivo.
   */
  permissionPreviewEnded?: AuthMePermissionPreviewEnded | null;
}

export interface AuthMePermissionPreviewEnded {
  /**
   * `PermissionPreviewEndReason` atteso, ma tipizzato come stringa: il backend
   * può introdurre motivi che il client non conosce ancora (→ testo generico).
   */
  reason: string;
}

const STALE_TIME = 30_000;
const GC_TIME = 300_000;

export const authMeQueryOptions = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: async (): Promise<AuthMeResponse> => {
    const response = await fetch(AUTH_ME_URL, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json;charset=UTF-8' },
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    return response.json();
  },
  staleTime: STALE_TIME,
  gcTime: GC_TIME,
});

export async function ensureAuthData(
  queryClient: QueryClient
): Promise<AuthMeResponse> {
  return queryClient.ensureQueryData(authMeQueryOptions);
}
